import React, { useState } from 'react';
import { useSpring, SpringValue } from '@react-spring/web';
import { useGesture } from 'react-use-gesture';
import { DragConfig, FullGestureState } from 'react-use-gesture/dist/types';
import { useInertialSpring } from 'hooks/useInertialSpring';
import { usePrevious } from 'utils/Index';
import { defaultSpringConfig } from 'components/Animations/SpringProperties/SpringProperties';

export interface ISpringInterface {
  [x: string]: SpringValue<any>;
  x: SpringValue<number>;
}

export interface IDraggableBounds {
  y: [number, number];
  x: [number, number];
}

export const useDraggable = (
  range: number = 0,
  bounds: IDraggableBounds = {
    y: [0, 0],
    x: [0, 0],
  },
) => {
  const previousRange = usePrevious(range);

  const [isBeingDragged, setIsBeingDragged] = useState(false);

  const [{ x: xPosition }, setPosition] = useInertialSpring({ x: 0, y: 0 });
  const [xVelocity, setXVelocity] = useSpring(() => ({ x: 0 }));

  const gestureOptions: DragConfig = {
    initial: () => [xPosition.get(), 0],
    rubberband: true,
    lockDirection: true,
    axis: 'x',
    bounds: {
      top: bounds.y[0],
      bottom: bounds.y[1],
      left: bounds.x[0],
      right: bounds.x[1],
    },
  };
  const bind = useGesture(
    { onDrag: state => handleDrag(state) },
    { drag: { ...gestureOptions, filterTaps: true } },
  );

  const handleDrag = (state: FullGestureState<'drag'>) => {
    updateSliderPosition(state);
  };

  const updateSliderPosition = (state: FullGestureState<'drag'>) => {
    const { down, movement: [mx, my], vxvy: [vx, vy], first, last } = state;

    const maxVelocity = 50;
    let scaledVelocity = vx * 40;

    if (scaledVelocity > maxVelocity)
      scaledVelocity = maxVelocity;
    else if (scaledVelocity < -maxVelocity)
      scaledVelocity = -maxVelocity;

    setXVelocity({ x: scaledVelocity });

    setPosition({
      x: mx,
      y: my,
      immediate: down,
      config: down ? { decay: false } : { inertia: true, bounds, velocity: { x: vx, y: vy } },
    });

    if (first)
      setIsBeingDragged(true);

    if (last) {
      setIsBeingDragged(false);
      setXVelocity({ x: 0 });
    }
  };

  React.useEffect(() => {
    if (previousRange !== undefined) {
      const oldRange = previousRange !== undefined ? previousRange : 100;
      if (range !== previousRange && range !== 0 && previousRange !== 0) {
        const currentXPosition = xPosition.get();
        const percentageXPosition = (currentXPosition / oldRange );
        setPosition({
          x: ( percentageXPosition * range),
          from: { x: (percentageXPosition * oldRange) * (oldRange / range) },
          immediate: false,
          config: { ...defaultSpringConfig },
        });
      }
    }
  }, [previousRange, range]);

  let returnValues: [(...args: any[]) => any, boolean, any, ISpringInterface, ({ config, ...to }: any) => void];
  returnValues = [bind, isBeingDragged, xPosition, xVelocity, setPosition];

  return returnValues;
};

